import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

// Page size is large enough to load all content_resource rows in one request so
// the UI can group by leading title prefix (or just by resource) without group
// blocks being split across pages. ~500 rows ≈ 90 resources × 5-6 fields covers
// the live LampTwist catalog with headroom.
const DEFAULT_PAGE_SIZE = 5000;

/**
 * Hook for the unified Articles + Pages content-resource inventory page
 * (Phase 18, Plan 18-03).
 *
 * Clone of useCollections.js with one additive delta:
 *   - NEW resourceType arg ('ARTICLE' | 'PAGE' | null) threaded into the
 *     request as `resource_type` query param. Backend filters
 *     WHERE resource_type = $resourceType when value is non-null.
 *   - Mounting the same hook behind two routes (/articles + /pages-content)
 *     is the D-06 unified-component pattern; passing the arg through makes
 *     each route feel like its own page.
 *
 * All other behaviour (fieldKey / policy / hasHtml / status / q with 300ms
 * debounce, optimistic policy PATCH with rollback, reset-to-page-1 on filter
 * changes, sync trigger + last-synced timestamp) mirrors useCollections.js
 * verbatim — only the URLs and the resourceType plumbing differ.
 *
 * Filters are SERVER-SIDE: every filter change refetches with query params,
 * matching Plan 18-01's API.
 */
export default function useContentResources(resourceType /* 'ARTICLE' | 'PAGE' | null */) {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (server-side per Plan 18-01)
  const [fieldKey, setFieldKey] = useState('');   // '' | 'title' | 'body_html' | 'summary_html' | 'handle' | 'meta_title' | 'meta_description'
  const [policy, setPolicy] = useState('all');    // 'all' | 'translate' | 'skip' | 'auto-skipped'
  const [hasHtml, setHasHtml] = useState('');     // '' | 'true' | 'false'
  const [status, setStatus] = useState('all');    // 'all' | 'missing'
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms — matches Phase 14 / Phase 15)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (resourceType) params.resource_type = resourceType;       // NEW vs useCollections (Phase 18 D-08)
      if (fieldKey) params.field_key = fieldKey;
      if (policy && policy !== 'all') params.policy = policy;
      if (hasHtml) params.has_html = hasHtml;
      if (status && status !== 'all') params.status = status;
      if (debouncedQ) params.q = debouncedQ;

      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/content-resources/', { params }),
        axios.get('/api/transly/content-resources/sync-status/'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load content resources');
    } finally {
      setLoading(false);
    }
  }, [resourceType, page, pageSize, fieldKey, policy, hasHtml, status, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters change (avoid stranded pages) — D-08:
  // include resourceType in deps so swapping between /articles and
  // /pages-content also resets pagination.
  useEffect(() => {
    setPage(1);
  }, [resourceType, fieldKey, policy, hasHtml, status, debouncedQ]);

  const updatePolicy = useCallback(async (contentResourceId, newPolicy) => {
    // Optimistic update — flip the row first, then PATCH; rollback on error.
    const prev = rows;
    setRows((curr) =>
      curr.map((r) =>
        r.id === contentResourceId
          ? {
              ...r,
              translation_policy: newPolicy,
              auto_skip_reason: newPolicy === null ? r.auto_skip_reason : null,
            }
          : r,
      ),
    );
    try {
      await axios.patch(`/api/transly/content-resources/${contentResourceId}/policy/`, {
        policy: newPolicy,
      });
      // For policy=null (reset to auto), the server re-classified — refetch to
      // learn the new auto_skip_reason.
      if (newPolicy === null) {
        await fetchData();
      }
    } catch (err) {
      // Rollback
      setRows(prev);
      throw err;
    }
  }, [rows, fetchData]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/content-resources/sync/');
  }, []);

  return {
    // Data
    rows,
    total,
    loading,
    error,
    // Filters
    fieldKey,
    setFieldKey,
    policy,
    setPolicy,
    hasHtml,
    setHasHtml,
    status,
    setStatus,
    q,
    setQ,
    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize,
    // Sync
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    triggerSync,
    // Actions
    updatePolicy,
    fetchData,
  };
}
